// =============================================================================
// Till preview door: the GUARD (special-card config, Phase 6, 28 Sep 2026).
//
// <host>/?preview=till, INSIDE A FRAME, turns this page into a picture of the
// REAL till screens for the Command Centre's card builder: the unmodified scan
// result, offline picker and offline result, and Customer Lookup, drawn from
// what the builder sends (src/tillPreview.jsx mounts them). This file only
// closes the page off, and it is main.jsx's FIRST import on purpose: its body
// runs before any other module (foh.jsx and every lib/ it pulls in) is
// evaluated, so nothing those modules do at load can read this shop's real
// storage or reach the network.
//
//   - Only inside a frame. Opened directly, ?preview=till boots the normal app
//     exactly as '/' does, so a crafted link can't paint made-up words onto a
//     real till screen.
//   - The data arrives ONLY by postMessage from the Command Centre
//     (https://admin.eatrolled.com) or a localhost harness. Nothing rides the URL.
//   - Storage: localStorage and sessionStorage are in-memory stores, document.cookie
//     reads '' and drops writes, and indexedDB refuses to open. The frame is
//     same-site with the Command Centre, so the browser would otherwise hand it
//     this shop's real staff session, cached card lists and offline queue.
//   - Network: window.fetch answers every call itself (the two Customer Lookup
//     calls from the builder's data; anything else gets a 400, never a 401, which
//     would start a token refresh). navigator.sendBeacon sends nothing, and
//     XMLHttpRequest, EventSource and WebSocket refuse every address. Nothing
//     leaves the frame.
//   - Focus: element.focus() does nothing here, so the Lookup search box's
//     autofocus can never pull the keyboard away from the builder field being
//     typed in.
//   If any of that can't be put in place, the door shows nothing (fail closed).
// =============================================================================

const W = typeof window !== "undefined" ? window : null;

const QUERY = (() => { try { return W ? new URLSearchParams(W.location.search) : null; } catch (e) { return null; } })();
const FRAMED = (() => { try { return !!W && W.self !== W.top; } catch (e) { return true; } })();

export const TILL_PREVIEW_DOOR = !!QUERY && QUERY.get("preview") === "till" && FRAMED;

// The till screens the door can draw. The builder picks one per message.
export const TILL_PREVIEW_VIEWS = ["scan", "offline", "offline_result", "lookup", "lookup_redeemed"];

// Who may drive the door: the live Command Centre, or a harness on this machine.
export function tillPreviewOriginOk(origin) {
  return origin === "https://admin.eatrolled.com"
    || /^http:\/\/(localhost|127\.0\.0\.1):\d+$/.test(String(origin || ""));
}

// A Storage-compatible in-memory store. getItem answers exactly null for a
// missing key, as the real one does.
export function makeMemoryStorage(seed) {
  const m = new Map();
  if (seed) Object.keys(seed).forEach((k) => { m.set(String(k), String(seed[k])); });
  return {
    getItem(k) { k = String(k); return m.has(k) ? m.get(k) : null; },
    setItem(k, v) { m.set(String(k), String(v)); },
    removeItem(k) { m.delete(String(k)); },
    clear() { m.clear(); },
    key(i) { const ks = Array.from(m.keys()); return (i >= 0 && i < ks.length) ? ks[i] : null; },
    get length() { return m.size; },
  };
}

// The door's world. `ok` is true only once every guard below is in place.
// `local` / `session` are the stores the page sees (tillPreview.jsx swaps in a
// freshly seeded pair for every message). `msg` is the latest accepted message.
export const tillPreviewWorld = { ok: false, local: null, session: null, msg: null, runs: 0, origin: null };

// Validates a message event from the builder. Returns the prepared message, or
// null if it isn't one the door accepts.
export function readTillPreviewMessage(e, parentWindow) {
  if (!e || e.source !== parentWindow || !tillPreviewOriginOk(e.origin)) return null;
  const m = e.data;
  if (!m || typeof m !== "object" || m.type !== "rolled-till-preview") return null;
  if (typeof m.rev !== "number" || !isFinite(m.rev)) return null;
  if (TILL_PREVIEW_VIEWS.indexOf(m.view) < 0) return null;
  if (!m.data || typeof m.data !== "object") return null;
  const ctx = (m.context && typeof m.context === "object") ? m.context : {};
  if (typeof ctx.card_type !== "string" || !ctx.card_type) return null;
  // The builder's highlight probe (a hidden copy of this phone): its markers, short
  // plain strings of its own, which the door looks for on screen and names back.
  const probe = Array.isArray(m.probe) ? m.probe.filter((s) => typeof s === "string" && s.length >= 4 && s.length <= 40).slice(0, 60) : null;
  return { rev: m.rev, view: m.view, data: m.data, cardType: ctx.card_type, ...(probe ? { probe } : {}) };
}

const subs = new Set();
export function subscribeTillPreview(fn) {
  subs.add(fn);
  return () => { subs.delete(fn); };
}

function acceptMessage(e) {
  const msg = W ? readTillPreviewMessage(e, W.parent) : null;
  if (!msg) return;
  tillPreviewWorld.runs += 1;
  tillPreviewWorld.origin = e.origin;
  tillPreviewWorld.msg = { ...msg, run: tillPreviewWorld.runs };
  subs.forEach((fn) => { try { fn(tillPreviewWorld.msg); } catch (err) {} });
}

// Tell the builder something (never any data from this page), aimed at the
// origin that last drove the door, or anyone for the first "ready".
export function tellTillPreviewBuilder(payload) {
  try { W.parent.postMessage(payload, tillPreviewWorld.origin || "*"); } catch (e) {}
}

function refuse() { throw new TypeError("Preview only"); }

function installSandbox() {
  tillPreviewWorld.local = makeMemoryStorage(null);
  tillPreviewWorld.session = makeMemoryStorage(null);
  const getLocal = () => tillPreviewWorld.local;
  const getSession = () => tillPreviewWorld.session;
  const getCookie = () => "";
  const dropCookie = () => {};
  const noDb = function tillPreviewNoIndexedDb() { throw new TypeError("Preview only"); };
  const idb = { open: noDb, deleteDatabase: noDb, databases: () => Promise.resolve([]), cmp: (a, b) => (a < b ? -1 : a > b ? 1 : 0) };
  try {
    Object.defineProperty(W, "localStorage", { configurable: true, enumerable: true, get: getLocal });
    Object.defineProperty(W, "sessionStorage", { configurable: true, enumerable: true, get: getSession });
    Object.defineProperty(W, "indexedDB", { configurable: true, enumerable: true, get: () => idb });
    Object.defineProperty(document, "cookie", { configurable: true, enumerable: true, get: getCookie, set: dropCookie });
    const dc = Object.getOwnPropertyDescriptor(document, "cookie");
    return W.localStorage === tillPreviewWorld.local && W.sessionStorage === tillPreviewWorld.session
      && W.indexedDB === idb && !!dc && dc.get === getCookie && document.cookie === "";
  } catch (e) {
    return false;
  }
}

function installFetchStub() {
  const reply = (status, body) => Promise.resolve(new Response(JSON.stringify(body), { status, headers: { "Content-Type": "application/json" } }));
  const stub = function tillPreviewFetch(input, init) {
    let url = null;
    try { url = new URL((input && typeof input === "object" && input.url) ? input.url : String(input), W.location.href); } catch (e) { url = null; }
    const m = tillPreviewWorld.msg;
    if (!url || !m || !tillPreviewWorld.ok) return reply(400, { error: "Preview only" });
    const method = String((init && init.method) || (input && typeof input === "object" && input.method) || "GET").toUpperCase();
    const path = url.pathname;
    // Customer Lookup: the search, then the Redeem button. Answered with the
    // builder's data, in the real routes' shapes (the backend shaped them).
    if (method === "GET" && /^\/api\/v1\/scanner\/lookup\/[^/]+$/.test(path) && m.data.lookup) return reply(200, m.data.lookup);
    if (method === "POST" && /^\/api\/v1\/staff\/customers\/[^/]+\/redeem-special$/.test(path) && m.data.redeem) return reply(200, m.data.redeem);
    return reply(400, { error: "Preview only" });
  };
  try { W.fetch = stub; } catch (e) {}
  return W.fetch === stub;
}

function sealNetwork() {
  const noBeacon = function tillPreviewSendBeacon() { return true; };
  const noOpen = function tillPreviewXhrOpen() { refuse(); };
  try { Object.defineProperty(navigator, "sendBeacon", { configurable: true, enumerable: true, writable: true, value: noBeacon }); } catch (e) {}
  try { if (W.XMLHttpRequest && W.XMLHttpRequest.prototype) W.XMLHttpRequest.prototype.open = noOpen; } catch (e) {}
  const sealed = {};
  ["EventSource", "WebSocket"].forEach((name) => {
    try {
      const Real = W[name];
      if (typeof Real !== "function") return;
      const Sealed = function () { refuse(); };
      Sealed.prototype = Real.prototype;
      Object.setPrototypeOf(Sealed, Real);
      W[name] = Sealed;
      sealed[name] = Sealed;
    } catch (e) {}
  });
  return navigator.sendBeacon === noBeacon
    && (!W.XMLHttpRequest || W.XMLHttpRequest.prototype.open === noOpen)
    && (typeof W.EventSource !== "function" || W.EventSource === sealed.EventSource)
    && (typeof W.WebSocket !== "function" || W.WebSocket === sealed.WebSocket);
}

function guardFocus() {
  const noFocus = function tillPreviewNoFocus() {};
  try { if (W.HTMLElement && W.HTMLElement.prototype) W.HTMLElement.prototype.focus = noFocus; } catch (e) {}
  try { if (W.SVGElement && W.SVGElement.prototype) W.SVGElement.prototype.focus = noFocus; } catch (e) {}
  return !W.HTMLElement || W.HTMLElement.prototype.focus === noFocus;
}

if (TILL_PREVIEW_DOOR) {
  const storageOk = installSandbox();
  const fetchOk = installFetchStub();
  const sealOk = sealNetwork();
  const focusOk = guardFocus();
  tillPreviewWorld.ok = storageOk && fetchOk && sealOk && focusOk;
  W.addEventListener("message", acceptMessage);
}
